import { useEffect, useState } from "react";
import { Navigate, NavigateProps, useParams } from "react-router-dom";
import {
  LoadingIndicator,
  defaultValue,
  Response,
  VesselsResponse,
  getVessels,
  useUserPermission,
  useAuthentication,
  getVesselsWithPermission,
  hasPermissionToCountry,
} from "@shape/reef-shared";
import { PrivateRoute } from "PrivateRoutes/PrivateRoute";
import { Routes, RoutesProps } from "routes";

type PrivateFacilitiesRouteProps = Partial<NavigateProps> &
  Partial<RoutesProps> & {
    element: JSX.Element;
    path?: string;
  };

export const PrivateFacilitiesRoute = ({ element, ...props }: PrivateFacilitiesRouteProps) => {
  const { userPermissions, isLoading } = useUserPermission();
  const { error: authError, isAuthenticated, isLoading: isAuthLoading } = useAuthentication();

  const vesselsWithPermission = getVesselsWithPermission(userPermissions.data?.user_groups ?? []);
  const [vesselsList, setVesselsList] = useState<Response<VesselsResponse[]>>(defaultValue);
  const params = useParams<{ country: string }>();
  const [previousLocation, setPreviousLocation] = useState<string | undefined>(undefined);

  useEffect(() => {
    setPreviousLocation(window.location.pathname || Routes.HomePage);
  }, []);

  useEffect(() => {
    if (!isLoading && !isAuthLoading) getVessels(setVesselsList);
  }, [isLoading, isAuthLoading]);

  const hasPermission = hasPermissionToCountry(
    params?.country ?? "",
    vesselsWithPermission,
    vesselsList.data
  );

  if (isAuthLoading) return <LoadingIndicator />;

  if (!isAuthenticated || authError)
    return <Navigate to={Routes.LoginPage} state={{ from: previousLocation }} />;

  if (vesselsList.data === undefined) return <LoadingIndicator />;

  return <PrivateRoute {...props} element={element} validation={() => hasPermission} />;
};
